import type { CountryCodeTBB, LangTBB } from '@src/i18n/locales'

export const COOKIES = {
  session: 'bodi-session',
  akamaiSession: 'bodi-akamai-session',
  /** The code verifier to use in the code exchange for the given code challenge ('PKCE') */
  akamaiCodeVerifier: 'bodi-akamai-code-verifier',
  /** Ensures the same callback URL is used for token request and code exchange */
  akamaiCallbackUrl: 'bodi-akamai-callback-url',
  /** Where to redirect user after auth flow is complete. */
  akamaiRedirectUrl: 'bodi-akamai-redirect-url',
  /** Skip SSO check on auth callback requests. */
  akamaiComingFromCallback: 'bodi-akamai-coming-from-callback',
  /** Ensures the SSO check is done once until a page is finally loaded. */
  akamaiCheckingSSO: 'bodi-akamai-checking-sso',
  order: 'bodi-order',
  passwordCreated: 'bodi-password-created',
  lastUrl: 'bodi-last-url',
  royaltyCode: 'bodi-royalty-code',
  royaltyCodeAlt: 'rc',
  webSourceCode: 'bodi-web-source-code',
  webSourceCodeAlt: 'code',
  upsellFromSkus: 'bodi-upsell-from-skus',
  upsellToSkus: 'bodi-upsell-to-skus',
  recaptcha: 'bodi-recaptcha',
  recaptchaToken: 'bodi-recaptcha-token',
  /** Tracks the redirects applied by the Flow Router (to expose Amplitude experiments and clear App Router cache). */
  flowRedirectApplied: 'bodi-flow-redirect-applied',
  userProperties: 'bodi-user-properties',
  inspectEnabled: 'bodi-inspect-enabled',
  affiliateClickId: 'bodi-affiliate-click-id',
  /** Tracks if the user has interacted with the geo redirection modal. Shows the modal if not. */
  geoRedirectionModal: 'bodi-geo-redirection-modal',
  lastActivityTimestamp: 'bodi-last-activity-timestamp',
}

export const QUERY_PARAMS = {
  gtmDisabled: 'bodi-gtm-disabled',
  locale: 'locale',
  referralProgramId: 'referralProgramId',
  trainerName: 'trainerName',
  royaltyCode: 'rc',
  sku: 'sku',
  quantity: 'quantity',
  redirectUrl: 'redirectUrl',
  akamaiLocale: 'akamaiLocale',
  affiliateClickId: 'irclickid',
  lcAmbassadorId: 'lc_ambassador_id',
  nextDoorClickId: 'ndclid' as const,
  bulk: 'bulk',
}

export const HEADERS = {
  acceptLanguage: 'accept-language',
  requestIp: 'bodi-request-ip',
  middlewareRewrite: 'bodi-middleware-rewrite',
  middlewareRedirect: 'bodi-middleware-redirect',
  locale: 'bodi-locale',
  method: 'bodi-method',
  url: 'bodi-url',
  host: 'bodi-host',
  lastUrl: 'bodi-last-url',
  vercelIPCountry: 'x-vercel-ip-country',
  vercelDeploymentUrl: 'x-vercel-deployment-url',
  cloudfrontHost: 'x-cloudfront-host',
}

export const CACHE = {
  page: 'page',
  notFoundSection: '404-section',
  flowRouterRules: 'flow-router-rules',
  nutritionPDP: 'nutrition-pdp',
  digitalPDP: 'digital-pdp',
  fitnessPDP: 'fitness-pdp',
}

export const STORAGE = {
  logsVisibleInConsole: 'logsVisibleInConsole',
  logLevelClient: 'logLevelClient',
  lastActivityTimestamp: 'lastActivityTimestamp',
  sseOrders: 'sseOrders',
}

export const EXPERIMENTS = {
  galleryExperiment: 'com-3086',
  cartFlyOut: 'com-3896',
  applePay: 'com-4454',
  applePayDropdownRadio: 'com-4427',
  sierra: 'com-4323',
}

// time and dates
export const ONE_SECOND = 1000
export const ONE_MINUTE = ONE_SECOND * 60
export const ONE_HOUR = ONE_MINUTE * 60
export const ONE_DAY = ONE_HOUR * 24
export const ONE_DAY_IN_SECONDS = ONE_DAY / 1000
export const ONE_YEAR = ONE_DAY * 365
export const ONE_YEAR_IN_SECONDS = ONE_YEAR / 1000

// 1px black image in base64
export const DEFAULT_IMG_URI =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAIAAAACCAYAAABytg0kAAAAAXNSR0IArs4c6QAAAAlwSFlzAAAWJQAAFiUBSVIk8AAAABNJREFUCB1jZGBg+A/EDEwgAgQADigBA//q6GsAAAAASUVORK5CYII%3D'

// errors
export enum ErrorCodes {
  CustomerEmailRequired = 'customer-email-required',
  IdentityOutage = 'identity-outage',
  DuplicateCustomerTypesGranted = 'duplicate-customer-types-granted',
  DuplicateEntitlementsGranted = 'duplicate-entitlements-granted',
  InvalidItems = 'invalid-items',
  InvalidQuantity = 'invalid-quantity',
  LineItemsRequired = 'line-items-required',
  InvalidShippingCountry = 'invalid-shipping-country',
  InvalidBillingCountry = 'invalid-billing-country',
  IncompleteShippingAddress = 'incomplete-shipping-address',
  ShippingMethodRequired = 'shipping-method-required',
  CcSystemError = 'cc-system-error',
  SystemError = 'system-error',
  InvalidShippingMethod = 'invalid-shipping-method',
  TaxRequired = 'tax-required',
  InvalidDiscountCodes = 'invalid-discount-codes',
  CustomerTypeConflict = 'customer-type-conflict',
  EntitlementConflict = 'entitlement-conflict',
  CcInvalidPayment = 'cc-invalid-payment',
  CcPaymentDeclined = 'cc-payment-declined',
  CcPayment3dsDeclined = 'cc-payment-3ds-declined',
  CcInvalid3dsPayment = 'cc-invalid-3ds-payment',
  CcRequired3dsPayment = 'cc-required-3ds-payment',
  ApplePayInit = 'apple-pay-init',
  CartMissing = '[client]-cart-missing',
  LineItemsWithErrors = '[client]-line-items-with-errors',
  AkamaiSSOFailure = 'akamai-sso-failure',
  AkamaiCallbackError = 'akamai-callback-validation-error',
  AkamaiSessionError = 'akamai-session-validation-error',
  AkamaiOAuthLogin = 'akamai-oauth-login-error',
  AkamaiOAuthInvalidCredentials = 'akamai-oauth-invalid-credentials-error',
  Cybersource3DSSetupFailed = 'cybersource-3ds-setup-failed',
  Cybersource3DSCheckEnrollmentFailed = 'cybersource-3ds-check-enrollment-failed',
  Cybersource3DSPayerValidationFailed = 'cybersource-3ds-payer-validation-failed',
  Unknown = 'unknown',
}

export enum ErrorType {
  Unrecoverable = 'Unrecoverable',
  Retry = 'Retry',
  Recalculate = 'Recalculate',
  InvalidItems = 'InvalidItems',
  Eligibility = 'Eligibility',
  Payment = 'Payment',
  Unknown = 'Unknown',
  Identity = 'Identity',
  ApplePay = 'ApplePay',
}

/**
 * Error codes grouped by error type.
 * Optionally used to treat multiple errors in a single way based on the error type.
 * @example: See src/app/[country]/[lang]/checkout/modules/ErrorModal.tsx
 */
export type ErrorsGroup = Partial<Record<ErrorType, ErrorCodes[]>>

export const BuilderActionErrorsGroup: ErrorsGroup = {
  [ErrorType.Eligibility]: [ErrorCodes.CustomerTypeConflict, ErrorCodes.EntitlementConflict],
  [ErrorType.InvalidItems]: [ErrorCodes.InvalidItems],
  [ErrorType.Unknown]: [ErrorCodes.Unknown],
}

export const SignUpErrorsGroup: ErrorsGroup = {
  [ErrorType.Identity]: [ErrorCodes.IdentityOutage],
}

export const CheckoutErrorsGroup: ErrorsGroup = {
  [ErrorType.Unrecoverable]: [
    ErrorCodes.CustomerEmailRequired,
    ErrorCodes.DuplicateCustomerTypesGranted,
    ErrorCodes.DuplicateEntitlementsGranted,
  ],
  [ErrorType.Retry]: [
    ErrorCodes.LineItemsRequired,
    ErrorCodes.InvalidShippingCountry,
    ErrorCodes.InvalidBillingCountry,
    ErrorCodes.IncompleteShippingAddress,
    ErrorCodes.ShippingMethodRequired,
    ErrorCodes.CcSystemError,
    ErrorCodes.SystemError,
    ErrorCodes.Cybersource3DSSetupFailed,
    ErrorCodes.Cybersource3DSCheckEnrollmentFailed,
    ErrorCodes.Cybersource3DSPayerValidationFailed,
  ],
  [ErrorType.Recalculate]: [
    ErrorCodes.InvalidShippingMethod,
    ErrorCodes.TaxRequired,
    ErrorCodes.InvalidDiscountCodes,
  ],
  [ErrorType.InvalidItems]: [ErrorCodes.InvalidItems, ErrorCodes.LineItemsWithErrors],
  [ErrorType.Eligibility]: [ErrorCodes.CustomerTypeConflict, ErrorCodes.EntitlementConflict],
  [ErrorType.Payment]: [
    ErrorCodes.CcInvalidPayment,
    ErrorCodes.CcPaymentDeclined,
    ErrorCodes.CcPayment3dsDeclined,
    ErrorCodes.CcInvalid3dsPayment,
    ErrorCodes.CcRequired3dsPayment,
  ],
  [ErrorType.ApplePay]: [ErrorCodes.ApplePayInit],
  [ErrorType.Unknown]: [ErrorCodes.Unknown],
}

export const OrderConfirmationErrorsGroup: ErrorsGroup = {
  [ErrorType.Unknown]: [ErrorCodes.Unknown],
}

export enum BuilderPageType {
  Landing = 'Landing',
  Offer = 'Offer',
  Upsell = 'Upsell',
  CreateAccount = 'Create Account',
  Category = 'Category',
  Cart = 'Cart',
  Other = 'Other',
}

export const CONSTANTS = {
  ONE_SECOND,
  ONE_MINUTE,
  ONE_HOUR,
  ONE_DAY,
  ONE_YEAR,
}

export enum CardType {
  Visa = 'Visa',
  Mastercard = 'Mastercard',
  AMEX = 'AMEX',
  Discover = 'DISCOVER',
}

export enum CardNetwork {
  VISA = 'VISA',
  MASTERCARD = 'MASTERCARD',
  AMEX = 'AMEX',
  DISCOVER = 'DISCOVER',
  CUP = 'CUP',
}

export const CardIcons: Record<CardType, string> = {
  [CardType.Visa]: '/images/visa.svg',
  [CardType.Mastercard]: '/images/mastercard.svg',
  [CardType.AMEX]: '/images/amex.svg',
  [CardType.Discover]: '/images/discover.svg',
}

export const CyberSourceCardTypeMapping: Record<string, CardType> = {
  '001': CardType.Visa,
  '002': CardType.Mastercard,
  '003': CardType.AMEX,
  '004': CardType.Discover,
  '062': CardType.Discover,
}

export enum PayerAuthActions {
  CONSUMER_AUTHENTICATION = 'CONSUMER_AUTHENTICATION',
  VALIDATE_CONSUMER_AUTHENTICATION = 'VALIDATE_CONSUMER_AUTHENTICATION',
  TOKEN_CREATE = 'TOKEN_CREATE',
}

export const PaymentConstants = {
  ONE_DOLLAR_CENT_AMOUNT: 101,
  EMPTY_VALUE: '--',
  INSTRUMENT_IDENTIFIER: 'instrumentIdentifier',
  COMMERCE_INDICATOR: 'internet',
}

export const MIDDLEWARE_TIMEOUTS = {
  sessionMiddleware: 5000,
  flowRouterMiddleware: 5000,
  akamaiMiddleware: 5000,
}

export const TBB_LANGUAGE_OPTIONS = [
  { countryCode: 'US', languages: ['en', 'es'] },
  { countryCode: 'CA', languages: ['en', 'fr'] },
  { countryCode: 'GB', languages: ['en'] },
  { countryCode: 'FR', languages: ['fr'] },
] satisfies Array<{ countryCode: CountryCodeTBB; languages: LangTBB[] }>

// Builder.io page fields used to fetch basic page data (used in sitemap and SEO)
export const BUILDER_PAGE_META_FIELDS = [
  'data.url',
  'data.title',
  'data.description',
  'data.openGraphImage',
  'data.robots',
  'data.canonicalUrl',
  'data.productKey',
  'data.seoStructuredData.includedInSitemap',
  'published',
  'query',
].join(',')
